import React, { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import { makeStyles } from '@material-ui/core/styles';
import { useMutation } from '@apollo/react-hooks';
import Grid from '@material-ui/core/Grid';
import Avatar from '@material-ui/core/Avatar';
import AccountBoxIcon from '@material-ui/icons/AccountBox';
import { t } from 'ttag';
import { createMutation } from '../UploadImage/UploadImage';
import Upload from '../Upload';
import LoadingButton from '../LoadingButton';

function readFile(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(reader.result), false);
    reader.readAsDataURL(file);
  });
}

const useStyles = makeStyles((theme) => ({
  cropContainer: {
    position: 'relative',
    width: '100%',
    height: 200,
    background: '#333',
    [theme.breakpoints.up('sm')]: {
      height: 400,
    },
  },
  large: {
    width: '177px',
    height: '236px',
  },
}));

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 * @param {File} image - Image File url
 * @param {Object} pixelCrop - pixelCrop Object provided by react-easy-crop
 * @param {number} rotation - optional rotation parameter
 */
export async function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const maxSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

  // set each dimensions to double largest dimension to allow for a safe area for the
  // image to rotate in without being clipped by canvas context
  canvas.width = safeArea;
  canvas.height = safeArea;

  // translate canvas context to a central location on image to allow rotating around the center.
  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate(getRadianAngle(rotation));
  ctx.translate(-safeArea / 2, -safeArea / 2);

  // draw rotated image and store data.
  ctx.drawImage(
    image,
    safeArea / 2 - image.width * 0.5,
    safeArea / 2 - image.height * 0.5
  );
  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  // set canvas width to final desired crop size - this will clear existing context
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // paste generated rotate image with correct offsets for x,y crop values.
  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
  );

  // As Base64 string
  // return canvas.toDataURL('image/jpeg');

  // As a blob
  return new Promise((resolve) => {
    canvas.toBlob((file) => {
      resolve(file);
    }, 'image/png');
  });
}

const ImageCropper = ({ name, fileObject, objectType, onChange }) => {
  const classes = useStyles();
  const [imageSrc, setImageSrc] = React.useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const [fileObj, setFileObject] = React.useState(fileObject);
  const [processedFile, setProcessedFile] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [savePhoto] = useMutation(createMutation);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const onSave = useCallback(async () => {
    setLoading(true);
    try {
      const croppedImage = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        rotation
      );
      const resFile = new File([croppedImage], processedFile.name);
      const { data } = await savePhoto({
        variables: {
          fileField: resFile,
          objectType,
        },
      });
      const { uploadFile } = data;
      if (uploadFile.ok) {
        setFileObject(uploadFile.fileObject);
        onChange({ target: { name, value: uploadFile.fileObject } });
        setImageSrc(null);
        setLoading(false);
      }
    } catch (e) {
      throw new Error('Error crop image');
    }
  }, [imageSrc, croppedAreaPixels, rotation]);

  const onFileChange = async (file) => {
    const imageDataUrl = await readFile(file);
    setProcessedFile(file);
    setImageSrc(imageDataUrl);
  };

  return (
    <div>
      {imageSrc ? (
        <Dialog open minWidth='sm' fullWidth onClose={() => setImageSrc(null)}>
          <DialogTitle
            id='customized-dialog-title'
            onClose={() => setImageSrc(null)}
          >
            {t`Завантажити фото`}
          </DialogTitle>
          <DialogContent dividers>
            <>
              <div className={classes.cropContainer}>
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  rotation={rotation}
                  zoom={zoom}
                  aspect={3 / 4}
                  onCropChange={setCrop}
                  onRotationChange={setRotation}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                />
              </div>
            </>
          </DialogContent>
          <DialogActions>
            <LoadingButton
              loading={loading}
              onClick={() => setImageSrc(null)}
              text={t`Відмінити`}
              variant='outlined'
              color='primary'
            />
            <LoadingButton
              loading={loading}
              onClick={onSave}
              text={t`Зберегти`}
            />
          </DialogActions>
        </Dialog>
      ) : (
        <div>
          <Upload id='uploadImage' onUpload={onFileChange}>
            <Button
              variant='outlined'
              color='primary'
              component='span'
              size='large'
            >
              {t`Завантажити`}
              <AccountBoxIcon style={{ marginLeft: '5px' }} />
            </Button>
          </Upload>
          <br />
          {fileObj && fileObj.url && (
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Avatar
                  variant='square'
                  src={fileObj.url}
                  className={classes.large}
                />
              </Grid>
            </Grid>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageCropper;
